import { formatMoney } from '@domain';
import { isFirmRole } from '@domain';
import { Icon } from '../components/Icon.js';
import { Card, PageTitle, Pill, buttonPrimary, buttonQuiet } from '../components/ui.js';
import type { IconKind } from '../data/forms.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import type { Action, AppState, Role } from '../state.js';
import type { ReferenceData } from '@domain';
import type { DashboardCounts } from '../api.js';
import type { ListFixture } from '../types.js';

interface Kpi {
  label: string;
  value: string;
  sub: string;
  tint: string;
  glyph: IconKind;
  glyphColor: string;
}

/*
  These were written into the file: twelve pushed today, two failed, three
  duplicates caught, a hundred and forty-two contacts. The same figures
  appeared for every company, on the live site, to anybody who opened the
  screen — including a client looking at their own books, where an invented
  number is not decoration but a false statement about their accounts.

  Everything here is now counted from what the system actually did. Two cards
  were removed rather than estimated: "Duplicates caught", because the
  duplicate check warns at the time and is not recorded anywhere, and
  "Tracking categories", because the reference load does not fetch them. An
  empty space is better than a number nobody can stand behind.
*/
function kpisFor(counts: DashboardCounts | null, t: Translate): Kpi[] {
  const n = (value: number) => (counts === null ? '—' : String(value));
  const pushed = counts?.pushedToday ?? 0;

  return [
    {
      label: 'Pushed to Xero today',
      value: n(pushed),
      sub:
        counts === null
          ? t('Loading')
          : pushed === 0
            ? t('Nothing filed yet today')
            : `${counts.pushedInvoices} ${t('invoices')} · ${counts.pushedBills} ${t('bills')}`,
      tint: '#EDF3EE',
      glyph: 'doc',
      glyphColor: '#35603F',
    },
    {
      label: 'Waiting in the queue',
      value: n(counts?.waiting ?? 0),
      sub:
        counts === null
          ? t('Loading')
          : `${counts.blocked} ${t('blocked')} · ${counts.inReview} ${t('needs review')}`,
      tint: '#EDF1F7',
      glyph: 'box',
      glyphColor: '#2B4568',
    },
    {
      label: 'Failed pushes',
      value: n(counts?.failed ?? 0),
      sub: counts === null ? t('Loading') : t('Not accepted by Xero'),
      tint: '#F8EDEC',
      glyph: 'plug',
      glyphColor: '#A94442',
    },
  ];
}

/** Counted from what was actually fetched, not from a figure typed here. */
function referenceRows(reference: ReferenceData) {
  return [
    { label: 'Contacts', endpoint: 'GET /Contacts', count: reference.contacts.length },
    { label: 'Accounts', endpoint: 'GET /Accounts', count: reference.accounts.length },
    { label: 'Tax rates', endpoint: 'GET /TaxRates', count: reference.taxRates.length },
    { label: 'Items', endpoint: 'GET /Items', count: reference.items.length },
    { label: 'Currencies', endpoint: 'GET /Currencies', count: reference.currencies.length },
  ];
}

interface DashboardProps {
  state: AppState;
  role: Role;
  orgName: string;
  invoices: ListFixture | undefined;
  counts: DashboardCounts | null;
  reference: ReferenceData;
  referenceLoadedAt: number | null;
  t: Translate;
  dispatch: (action: Action) => void;
}

/** "14 minutes ago" used to be written in the file. This reads the clock. */
function howLongAgo(at: number | null, t: Translate): string {
  if (at === null) return t('Not loaded yet');
  const minutes = Math.floor((Date.now() - at) / 60_000);
  if (minutes < 1) return t('Cached just now');
  if (minutes === 1) return t('Cached 1 minute ago');
  return `${t('Cached')} ${minutes} ${t('minutes ago')}`;
}

export function Dashboard({
  state,
  role,
  orgName,
  invoices,
  counts,
  reference,
  referenceLoadedAt,
  t,
  dispatch,
}: DashboardProps) {
  const isAdmin = isFirmRole(role);
  const kpis = kpisFor(counts, t);

  const recent = (invoices?.rows ?? []).slice(0, 5);

  /*
    Found rather than counted to. This read row[6] until an Outstanding column
    was added to the invoice list, after which it rendered the amount
    outstanding as though it were a status — a number in a coloured pill,
    which looks deliberate enough that nobody would query it.

    The list screen already locates the status by the column marked as a pill.
    Doing the same here means a column added tomorrow moves both.
  */
  const statusIndex = (invoices?.columns ?? []).findIndex((c) => c.pill);

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1500px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', marginBottom: '20px' }}>
        <PageTitle
          title={t('Dashboard')}
          sub={`${orgName} · ${t(isAdmin ? 'QAR · financial year to 31 December 2026' : 'QAR · your submissions')}`}
        />
        <div style={{ flex: 1 }} />
        <button onClick={() => dispatch({ type: 'go', screen: 'form:invoice' })} style={buttonPrimary}>
          {t('New invoice')}
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))',
          gap: '14px',
          marginBottom: '20px',
        }}
      >
        {kpis.map((kpi) => (
          <Card key={kpi.label} style={{ padding: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: theme.radiusControl,
                  background: kpi.tint,
                  display: 'grid',
                  placeItems: 'center',
                  flex: '0 0 auto',
                }}
              >
                <Icon kind={kpi.glyph} color={kpi.glyphColor} />
              </div>
            </div>
            <div
              style={{
                fontFamily: theme.fontMono,
                fontSize: '25px',
                fontWeight: 500,
                marginTop: '16px',
                letterSpacing: '-.02em',
              }}
            >
              {kpi.value}
            </div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: theme.text, marginTop: '8px' }}>
              {t(kpi.label)}
            </div>
            <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '3px' }}>{t(kpi.sub)}</div>
          </Card>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))',
          gap: '16px',
          alignItems: 'start',
        }}
      >
        <Card>
          <div
            style={{
              padding: '13px 16px',
              borderBottom: `1px solid ${theme.dividerSoft}`,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: '13.5px' }}>{t('Recent submissions')}</div>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => dispatch({ type: 'go', screen: 'list:invoices' })}
              style={{ background: 'transparent', border: 0, color: theme.ink, fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}
            >
              {t('View all')}
            </button>
          </div>
          {recent.map((row) => {
            const key = `dash:${String(row[0])}`;
            const status = state.reads[key] ?? (statusIndex < 0 ? '' : String(row[statusIndex]));
            return (
              <div
                key={String(row[0])}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '96px minmax(0,1fr) 82px 96px auto',
                  gap: '10px',
                  alignItems: 'center',
                  padding: '11px 16px',
                  borderBottom: `1px solid ${theme.divider}`,
                  fontSize: '12.5px',
                }}
              >
                <span style={{ fontFamily: theme.fontMono, color: theme.ink, fontWeight: 500 }}>{String(row[0])}</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(row[1])}</span>
                <span style={{ color: theme.textMuted, fontFamily: theme.fontMono }}>{String(row[3])}</span>
                <span style={{ fontFamily: theme.fontMono, textAlign: 'end' }}>{formatMoney(Number(row[5]))}</span>
                <span style={{ justifySelf: 'end' }}>
                  <Pill status={status} label={t(status)} />
                </span>
              </div>
            );
          })}
          <div style={{ padding: '12px 16px', fontSize: '11.5px', color: theme.textSubtle, lineHeight: 1.6 }}>
            {t('Statuses are read one record at a time when you refresh a row — nothing is mirrored in the background.')}
          </div>
        </Card>

        {isAdmin && (
          <Card>
            <div style={{ padding: '13px 16px', borderBottom: `1px solid ${theme.dividerSoft}`, fontWeight: 600, fontSize: '13.5px' }}>
              {t('Xero reference data')}
            </div>
            {referenceRows(reference).map((row) => (
              <div
                key={row.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 16px',
                  borderBottom: `1px solid ${theme.divider}`,
                  fontSize: '12.5px',
                }}
              >
                <span style={{ flex: 1 }}>{t(row.label)}</span>
                <span style={{ fontFamily: theme.fontMono, color: theme.textMuted, fontSize: '11.5px' }}>{row.endpoint}</span>
                <span style={{ fontFamily: theme.fontMono, color: theme.text }}>{row.count}</span>
              </div>
            ))}
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button style={buttonQuiet}>{t('Re-sync now')}</button>
              <span style={{ fontSize: '12px', color: '#5A6676' }}>
                {howLongAgo(referenceLoadedAt, t)}
              </span>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
